import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { Selectors } from '@grandvisionhq/auth';
import { toast } from '@grandvisionhq/common';
import { Util } from '@grandvisionhq/forms';
import { WithUserData } from '@grandvisionhq/my-account';
import { useIntl, useDispatch, useSelector, useFeatureToggles } from '@grandvisionhq/state';
import { init, initOrder } from '../actions.js';
import { useCheckoutSettings } from '../context/CheckoutSettings/context.js';
import '../models/cart/cart-state.js';
import '../models/checkout/checkout-step-error-type.js';
import '../models/checkout-settings.js';
import { ShippingMethod } from '../models/step-shipping/shipping-methods.js';
import '../models/shared.js';
import { getServiceStore, shouldCreateAccount } from '../selectors.js';
import { useFormSlot } from '../slots/forms/form-context.js';
import '../slots/summaries/summary-context.js';
import '../slots/summaries/types.js';
import { CustomerType } from '../types.js';
import 'lodash/isEmpty';
import '@grandvisionhq/utils';
import '../mappers/errors-mapping.js';
import '../utils/get-customer-details-payload.js';
import { isDelivery, isDeliveryExpress } from '../utils/shipping-method.js';
import 'history';
import '../utils/has-cart-errors.js';
import { resetMyAccountValidation, toggleDeliveryAddress, toggleIsCompany, markFieldsTouched, setMyAccountValidation, setServiceStoreValidation } from './actions.js';
import { useFormPrefill } from './hooks/use-form-prefill.js';
import { getCompany, isBillingAndShippingAddressSame, isShippingStepValid, isPrefillFinished, getSelectedShippingMethod, hasStoredDeliveryAddress, getBillingAddress } from './selectors.js';
import { StepShipping } from './StepShipping.js';

const StepShippingContainer = ({ onNextStep , onPrevStep , shippingMethodsAsides , userData ,  })=>{
    var ref6, ref1, ref2, ref3, ref4, ref5;
    const checkoutSettings = useCheckoutSettings();
    const { getLabel  } = useIntl();
    const dispatch = useDispatch();
    useFormPrefill(userData);
    const company = useSelector(getCompany);
    const isLoggedIn = useSelector(Selectors.isLoggedIn);
    const billingAndDeliverySame = useSelector(isBillingAndShippingAddressSame);
    const { billingAddress , deliveryAddress  } = useFormSlot();
    const isShippingStepValidBoolean = useSelector(isShippingStepValid);
    const isPrefillFinished$1 = useSelector(isPrefillFinished);
    const selectedShippingMethod = useSelector(getSelectedShippingMethod);
    const selectedServiceStore = useSelector(getServiceStore);
    const shouldCreateAccountBoolean = useSelector(shouldCreateAccount);
    const hasStoredDeliveryAddressBoolean = useSelector(hasStoredDeliveryAddress);
    const billingAddressDetails = useSelector(getBillingAddress);
    const isCreateAccountRequired = Boolean(((ref6 = checkoutSettings.account) === null || ref6 === void 0 ? void 0 : ref6.implicitAccountCreation) || ((ref1 = checkoutSettings.account) === null || ref1 === void 0 ? void 0 : ref1.createAccountRequired));
    const [isOptInsFetching, setIsOptInsFetching] = React.useState(true);
    const isCompanyEnabled = (ref2 = checkoutSettings.features) === null || ref2 === void 0 ? void 0 : ref2.isCompanyEnabled;
    const isServiceStoreEnabled = (ref3 = checkoutSettings.features) === null || ref3 === void 0 ? void 0 : ref3.isServiceStoreEnabled;
    const isDeliveryAddressRequired = (selectedShippingMethod === null || selectedShippingMethod === void 0 ? void 0 : selectedShippingMethod.method) === ShippingMethod.Delivery && ((ref4 = checkoutSettings.shipping) === null || ref4 === void 0 ? void 0 : ref4.requiredShippingCountry) && (billingAddressDetails === null || billingAddressDetails === void 0 ? void 0 : billingAddressDetails.country) && checkoutSettings.shipping.requiredShippingCountry !== billingAddressDetails.country;
    const isStepValid = ()=>{
        var ref;
        return isShippingStepValidBoolean && (!isServiceStoreEnabled || !!selectedServiceStore || isServiceStoreEnabled && Boolean((ref = checkoutSettings.serviceStore) === null || ref === void 0 ? void 0 : ref.isOptional)) && (isLoggedIn || !isCreateAccountRequired || shouldCreateAccountBoolean);
    };
    React.useEffect(()=>{
        if (!isCreateAccountRequired) {
            dispatch(resetMyAccountValidation());
        }
    // This should be called only when createAccountRequired flag changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isCreateAccountRequired
    ]);
    const onToggleIsCompany = (radio)=>{
        dispatch(toggleIsCompany({
            enabled: radio.value === CustomerType.Company
        }));
    };
    const onToggleDeliveryAddress = ({ checked  })=>{
        if (isDeliveryAddressRequired) {
            toast.failed('checkout.address.requiredShippingCountry.errorMessage');
        } else {
            dispatch(toggleDeliveryAddress({
                enabled: !checked
            }));
        }
    };
    React.useEffect(()=>{
        dispatch(init());
        dispatch(initOrder());
    }, [
        dispatch
    ]);
    React.useEffect(()=>{
        /**
     * To ensure that the delivery address is prefilled, it is necessary to  enable the
     * delivery form in advance. However, it is important to note that this requirement
     * is applicable only to the "Delivery" and "DeliveryExpress" shippingMethod options.
     */ if (!billingAndDeliverySame && hasStoredDeliveryAddressBoolean && ((selectedShippingMethod === null || selectedShippingMethod === void 0 ? void 0 : selectedShippingMethod.method) === ShippingMethod.Delivery || (selectedShippingMethod === null || selectedShippingMethod === void 0 ? void 0 : selectedShippingMethod.method) === ShippingMethod.DeliveryExpress)) {
            dispatch(toggleDeliveryAddress({
                enabled: true
            }));
        }
    /**
     *  This hook should only be executed during the on mount process.
     */ // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    React.useEffect(()=>{
        if (isDeliveryAddressRequired) {
            dispatch(toggleDeliveryAddress({
                enabled: true
            }));
        }
    }, [
        isDeliveryAddressRequired,
        dispatch
    ]);
    const nextStep = ()=>{
        if (!isStepValid()) {
            var ref;
            dispatch(markFieldsTouched());
            if (isCreateAccountRequired && !shouldCreateAccountBoolean) {
                dispatch(setMyAccountValidation());
            }
            if (isServiceStoreEnabled && !((ref = checkoutSettings.serviceStore) === null || ref === void 0 ? void 0 : ref.isOptional)) {
                dispatch(setServiceStoreValidation());
            }
            toast.failed('checkout.errors.form.general');
            Util.focusToTopErrorFieldOnScreen();
        } else {
            onNextStep();
        }
    };
    /**
   *  Callback from OptInContainer to return
   *  loading state of OptIns from GraphQL call
   *
   *  @param isOptInsLoading
   *    flag to return loading state of OptIns
   */ const handleOptInsLoading = React.useCallback(({ isOptInsLoading  })=>{
        setIsOptInsFetching(isOptInsLoading);
    }, []);
    const showLoader = isOptInsFetching || !isPrefillFinished$1;
    const labels = React.useMemo(()=>({
            generic: {
                title: getLabel('checkout.generic.title')
            },
            customerType: {
                private: getLabel('checkout.customerType.private'),
                company: getLabel('checkout.customerType.company')
            },
            shipmentMethodTitle: getLabel('checkout.shipment.method.title'),
            serviceStoreTitle: getLabel('checkout.serviceStore.title'),
            serviceStoreSubtitle: getLabel('checkout.serviceStore.subtitle')
        })
    , [
        getLabel
    ]);
    return /*#__PURE__*/ React.createElement(StepShipping, {
        billingAddressForm: billingAddress,
        deliveryAddressForm: deliveryAddress,
        company: Boolean(company),
        isCompanyEnabled: Boolean(isCompanyEnabled),
        isDelivery: isDelivery(selectedShippingMethod === null || selectedShippingMethod === void 0 ? void 0 : selectedShippingMethod.method),
        isDeliveryExpress: isDeliveryExpress(selectedShippingMethod === null || selectedShippingMethod === void 0 ? void 0 : selectedShippingMethod.method),
        labels: labels,
        onNextStep: nextStep,
        onPrevStep: onPrevStep,
        routes: checkoutSettings === null || checkoutSettings === void 0 ? void 0 : checkoutSettings.routes,
        shippingMethodsAsides: shippingMethodsAsides,
        showLoader: showLoader,
        toggleDeliveryAddress: onToggleDeliveryAddress,
        toggleIsCompany: onToggleIsCompany,
        handleOptInsLoading: handleOptInsLoading,
        enableServiceStoreValidation: !((ref5 = checkoutSettings.serviceStore) === null || ref5 === void 0 ? void 0 : ref5.isOptional)
    });
};
const StepShippingContainerWithUserData = WithUserData(StepShippingContainer);
const StepShippingContainerWithMyAccount = (_param)=>{
    var props = swcHelpers.extends({}, _param);
    const { isMyAccountEnabled  } = useFeatureToggles();
    return /*#__PURE__*/ React.createElement(StepShippingContainerWithUserData, swcHelpers.extends({}, props, {
        isMyAccountEnabled: isMyAccountEnabled
    }));
};

export { StepShippingContainerWithMyAccount as default };
//# sourceMappingURL=StepShippingContainer.js.map
